import Image from '@next/image'
import { SyntheticEvent, useEffect, useRef, useState } from 'react'
import { useMedia } from 'hooks/useMedia'
import ReactModal from 'react-modal'
import { Icon } from 'elements/Icon/Icon'
import { ImageType } from 'typings/image'
import Slider from 'react-slick'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { Pagination } from 'swiper'
import clsx from 'clsx'

SwiperCore.use([Pagination])

type Props = { productImages: ImageType[] }

export const MediaGallery = (props: Props) => {
  const isDesktop = useMedia('lg')
  useEffect(() => {
    const rootElement = document.getElementById('__next')
    if (rootElement) {
      ReactModal.setAppElement(rootElement)
    }
  }, [])
  /**
   * This the the slider where you can drag the images horizontally
   */
  const MobileMediaGallery = ({ productImages }: Props) => {
    return (
      <div className="mb-6 w-screen px-4">
        <Swiper
          pagination={{
            clickable: false,
            renderBullet: function (index, className) {
              return '<span class="' + className + '"></span>'
            },
          }}
        >
          <div className="swiper-slide-wrapper" />
          {productImages.map((image, index) => (
            <SwiperSlide key={`${image.uuid}-${index}`}>
              <Image src={image.url} alt={image.alt} width={384} height={384} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    )
  }

  type SliderArrowProps = {
    direction: 'up' | 'down'
    onClick: () => void
  }

  const SliderArrowButton = ({ direction, onClick }: SliderArrowProps) => {
    return (
      <div
        className="mb-4 flex h-4 w-full cursor-pointer items-center justify-center bg-red"
        onClick={onClick}
      >
        <Icon name={`chevron-${direction}`} className="flex-shrink-0 text-white" />
      </div>
    )
  }

  /**
   * This is the Desktop Gallery with the thumbnails
   */
  const DesktopMediaGallery = ({ productImages }: Props) => {
    const slider = useRef(null)
    const [firstSlideActive, setFirstSlideActive] = useState(true)
    const [lastSlideActive, setLastSlideActive] = useState(false)
    const [modalImage, setModalImage] = useState<ImageType | false>(false)
    const [largeImage, setLargeImage] = useState<ImageType>(productImages[0])
    const [showFullsize, setShowFullsize] = useState(false)
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
    const IMAGES_TO_SHOW = 4
    const settings = {
      arrows: false,
      dots: false,
      infinite: false,
      speed: 800,
      slidesToShow: IMAGES_TO_SHOW,
      slidesToScroll: 1,
      vertical: true,
      verticalSwiping: true,
      draggable: false,
      beforeChange: (_currentSlide: number, nextSlide: number) => {
        setFirstSlideActive(nextSlide === 0)
        setLastSlideActive(nextSlide === productImages.length - IMAGES_TO_SHOW)
      },
    }
    const gallery = productImages.length > 1

    const handleOpenModal = (image: ImageType) => {
      setModalImage(image)
    }

    const handleCloseModal = () => {
      setModalImage(false)
    }

    const toggleFullsize = () => {
      setShowFullsize((prev) => !prev)
    }

    const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
      setDimensions({
        width: event.currentTarget.naturalWidth,
        height: event.currentTarget.naturalHeight,
      })
    }

    return (
      <div
        className={clsx(
          'ml-20 mr-10 grid grid-rows-1 gap-8',
          gallery ? 'grid-cols-[80px,auto]' : 'grid-cols-1',
        )}
      >
        {gallery ? (
          <div className="flex flex-col">
            {!firstSlideActive && (
              <SliderArrowButton direction="up" onClick={() => slider.current.slickPrev()} />
            )}
            <Slider
              {...settings}
              ref={slider}
              className={productImages.length <= 3 ? 'max-h-[300px]' : 'max-h-[400px]'}
            >
              {productImages.map((image, index) => (
                <button
                  key={`${image.uuid}-${index}`}
                  className={clsx(
                    'mb-4 border',
                    image.uuid === largeImage.uuid ? 'border-red' : 'border-gray',
                  )}
                  onClick={() => setLargeImage(image)}
                >
                  {image.url ? (
                    <Image
                      src={image.url}
                      alt={image.alt}
                      width={1}
                      height={1}
                      layout="responsive"
                    />
                  ) : null}
                </button>
              ))}
            </Slider>
            {!lastSlideActive && productImages.length > IMAGES_TO_SHOW && (
              <SliderArrowButton
                direction="down"
                onClick={() =>
                  lastSlideActive ? slider.current.slickGoTo(0) : slider.current.slickNext()
                }
              />
            )}
          </div>
        ) : null}
        <div className="self-start">
          {largeImage ? (
            <button
              onClick={() => handleOpenModal(largeImage)}
              className="relative h-full w-full cursor-[zoom-in] border border-gray"
            >
              <Image
                src={largeImage.url}
                alt={largeImage.alt}
                width={1}
                height={1}
                layout="responsive"
              />
            </button>
          ) : null}
        </div>
        <ReactModal
          isOpen={!!modalImage}
          onRequestClose={handleCloseModal}
          style={{
            overlay: { zIndex: 51, backgroundColor: 'rgba(0,0,0,0.75)' },
            content: { zIndex: 52 },
          }}
        >
          <div
            className={clsx(
              'relative grid h-full w-full overflow-hidden p-4',
              showFullsize ? 'grid-cols-1' : 'grid-cols-[80px,1fr]',
            )}
          >
            <button
              className="absolute top-4 right-4 z-10 transition-transform hover:scale-125"
              onClick={handleCloseModal}
            >
              <Icon name="cross" />
            </button>
            {showFullsize ? null : (
              <ul className="flex flex-col self-center">
                {productImages.map((image, i) =>
                  image ? (
                    <li key={`${image.uuid}-${i}`} className="mb-4">
                      <button
                        onClick={() => setModalImage(image)}
                        className={clsx(
                          'relative h-full w-full border',
                          modalImage && image.uuid === modalImage.uuid
                            ? 'border-red'
                            : 'border-gray',
                        )}
                      >
                        <Image
                          src={image.url}
                          alt={image.alt}
                          width={1}
                          height={1}
                          layout="responsive"
                        />
                      </button>
                    </li>
                  ) : null,
                )}
              </ul>
            )}
            {modalImage && modalImage?.url.length ? (
              <button
                onClick={toggleFullsize}
                className={clsx(
                  'relative',
                  showFullsize ? 'cursor-[zoom-out]' : ' cursor-[zoom-in]',
                )}
              >
                {modalImage ? (
                  showFullsize ? (
                    <div
                      style={{ width: dimensions.width, height: dimensions.height }}
                      className="absolute top-2/4 left-2/4 -translate-x-2/4 -translate-y-2/4"
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={modalImage.url}
                        alt={modalImage.alt}
                        className="h-full w-full"
                        onLoad={handleImageLoad}
                      />
                    </div>
                  ) : (
                    <Image
                      src={modalImage.url}
                      alt={modalImage.alt}
                      fill
                      style={{ objectFit: 'cover' }}
                    />
                  )
                ) : null}
              </button>
            ) : null}
          </div>
        </ReactModal>
      </div>
    )
  }

  return isDesktop ? <DesktopMediaGallery {...props} /> : <MobileMediaGallery {...props} />
}
